import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@change/ds-core-ui/Modal';
import { Spinner } from '@change/ds-core-ui/Spinner';
import { Text } from '@change/ds-core-ui/Text';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionVideoSurface, SupporterVideo } from '../../../../types';

import { PetitionVideoDeleteMessage } from './components/PetitionVideoDeleteMessage';
import { useDeleteVideoLogic } from './hooks/useDeleteVideoLogic';

type DeleteVideoModalProps = {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	video: SupporterVideo;
	petitionId: string;
	renderedOn: PetitionVideoSurface;
	onResult: (result: { value: 'success' | 'error' | undefined }) => void;
};

export const DeleteVideoModal: React.FC<DeleteVideoModalProps> = ({
	isOpen,
	onOpenChange,
	video,
	petitionId,
	renderedOn,
	onResult,
}) => {
	const { isDeleting, deleteMessage, handleCancel, handleDelete } = useDeleteVideoLogic({
		video,
		petitionId,
		renderedOn,
		onResult,
		onOpenChange,
	});

	const { translate } = useI18n();

	const title = translate('corgi.petition_feed.delete_video.heading');

	return (
		<Modal isOpen={isOpen} onOpenChange={onOpenChange} isDismissable={false} isKeyboardDismissDisabled>
			<ModalHeader title={title} />
			<ModalBody>
				<div data-testid="delete-confirmation" className="flex flex-col">
					<Text variant="body" className="mb-8">
						<Translate value="corgi.petition_feed.delete_video.subtitle" />
					</Text>
					<div className="flex justify-center">
						{isDeleting && <Spinner className="mb-4" data-testid="delete-loader" />}
					</div>
					<PetitionVideoDeleteMessage message={deleteMessage} />
				</div>
			</ModalBody>
			{deleteMessage !== 'success' && (
				<ModalFooter>
					{() => (
						<>
							<Button
								variant="primary"
								data-testid="confirm-delete-btn"
								data-qa="confirm-delete-btn"
								onClick={handleDelete}
								disabled={isDeleting}
								className="uppercase"
							>
								<Translate value="corgi.components.petition_video.delete.confirm" />
							</Button>
							<Button
								variant="secondary"
								data-testid="confirm-cancel-btn"
								data-qa="confirm-cancel-btn"
								onClick={handleCancel}
								disabled={isDeleting}
							>
								<Translate value="corgi.components.petition_video.delete.cancel" />
							</Button>
						</>
					)}
				</ModalFooter>
			)}
		</Modal>
	);
};
